"use client";

/**
 * Shared DownloadButton component
 * Supports free / name-your-price (PWYW) / fixed-price downloads.
 * Handles Stripe checkout and Swish (Swedish users).
 * Creates a numbered download certificate (edition number) on every download.
 *
 * ALL downloads require payment — even "free" typed tracks go through the
 * PWYW dialog so the artist always earns and every download is logged.
 *
 * Format picker: when both a hi-res (WAV/FLAC) and a compressed (MP3/M4A) URL
 * are present, the user chooses which format to download. The selection is
 * forwarded to the Stripe session metadata and then to the download API.
 *
 * Minimum: STRIPE_FLOOR_CENTS (100 = €1.00) applies to all types.
 */

/** Minimum cents charged for any download — ensures the artist earns after payment fees */
const STRIPE_FLOOR_CENTS = 100; // €1.00 / $1.00 / £1.00

/** All supported format labels keyed by file extension */
export const FORMAT_LABELS: Record<string, string> = {
  // Audio
  mp3: "MP3", wav: "WAV", flac: "FLAC", m4a: "M4A",
  aac: "AAC", ogg: "OGG", aif: "AIFF", aiff: "AIFF",
  opus: "OPUS", wma: "WMA", alac: "ALAC",
  // MIDI
  mid: "MIDI", midi: "MIDI",
  // Video
  mp4: "MP4", mov: "MOV", avi: "AVI", mkv: "MKV",
  webm: "WEBM", wmv: "WMV",
  // Documents
  pdf: "PDF", doc: "DOC", docx: "DOCX", txt: "TXT",
  ppt: "PPT", pptx: "PPTX", xls: "XLS", xlsx: "XLSX",
  // Archives / bundles
  zip: "ZIP", rar: "RAR", "7z": "7Z", tar: "TAR", gz: "GZ",
  // Images / design
  png: "PNG", jpg: "JPG", jpeg: "JPG", gif: "GIF",
  svg: "SVG", webp: "WEBP", psd: "PSD", ai: "AI", eps: "EPS",
  // Software / installers
  exe: "EXE", dmg: "DMG", pkg: "PKG", msi: "MSI",
  apk: "APK", appimage: "AppImage", deb: "DEB", rpm: "RPM",
  // DAW / plugin
  vst: "VST", vst3: "VST3", au: "AU", aax: "AAX",
  // Data
  json: "JSON", xml: "XML", csv: "CSV",
};

/** Derive a short uppercase format label from a download URL */
export function getFormatLabel(url: string, fallback = "FILE"): string {
  const clean = url.split("?")[0].toLowerCase();
  const ext = clean.match(/\.([a-z0-9]+)$/)?.[1] || "";
  return FORMAT_LABELS[ext] || (ext ? ext.toUpperCase() : fallback);
}

/** Returns true for audio-only extensions (drives ffmpeg cert embedding) */
export function isAudioFormat(url: string): boolean {
  const clean = url.split("?")[0].toLowerCase();
  const ext = clean.match(/\.([a-z0-9]+)$/)?.[1] || "";
  return ["mp3","wav","flac","m4a","aac","ogg","aif","aiff","opus","wma","alac"].includes(ext);
}

import { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/auth-context";
import { useIsSwedishUser } from "@/hooks/use-swedish-user";
import { SwishCheckoutDialog } from "@/components/payment/swish-checkout-dialog";
import { cn } from "@/lib/utils";

export interface DownloadFile {
  url: string;
  /** Display name shown to buyer (e.g. "Sample Pack", "License PDF") */
  fileName: string;
  /** Explicit format label override — falls back to URL extension detection */
  format?: string;
  /** File size in bytes for display */
  sizeBytes?: number;
}

export interface DownloadItem {
  id: string;
  title: string;
  artist?: string;
  coverArtUrl?: string;
  /** Defaults to true — only hidden when explicitly false */
  allowDownloads?: boolean;
  /** Defaults to 'pwyw' */
  downloadType?: "free" | "pwyw" | "paid";
  /** Min price in cents */
  downloadMinPrice?: number;
  downloadCurrency?: string;
  /** Lossless/hi-res audio URL (WAV, FLAC, AIFF) */
  hiResUrl?: string;
  /** Video URL (MP4, MOV) */
  videoUrl?: string;
  /** Primary compressed audio URL (MP3, M4A, AAC) */
  audioUrl?: string;
  /**
   * Generic single-file download URL — for any non-audio content:
   * PDF, ZIP, EXE, DMG, PSD, MIDI, etc.
   * When set, overrides hiResUrl/audioUrl format detection.
   */
  fileUrl?: string;
  /** Explicit human-readable label for fileUrl (e.g. "VST Plugin", "Sample Pack ZIP") */
  fileLabel?: string;
  /**
   * Marketplace multi-file bundles — shown as an indexed file list in the
   * payment dialog. Each file is fetched from the download API after payment.
   */
  downloadFiles?: DownloadFile[];
}

interface DownloadButtonProps {
  item: DownloadItem;
  /** Extra tracks — if any allow downloads, the button shows */
  tracks?: DownloadItem[];
  /** Compact icon-only mode for use in cards */
  compact?: boolean;
  className?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  eur: "€",
  usd: "$",
  gbp: "£",
  cad: "$",
  sek: "kr",
};

function displayPrice(cents: number) {
  return (cents / 100).toFixed(2);
}

export function DownloadButton({
  item,
  tracks = [],
  compact = false,
  className,
}: DownloadButtonProps) {
  const { userProfile, user } = useAuth();
  const isSwedish = useIsSwedishUser();
  const router = useRouter();

  // allowDownloads defaults to true — only hidden when explicitly set to false
  const allowDl =
    item.allowDownloads !== false ||
    (tracks.length > 0 && tracks.some((t) => t.allowDownloads !== false));

  // downloadType defaults to pwyw
  const downloadType = item.downloadType || tracks[0]?.downloadType || "pwyw";
  const minPrice = item.downloadMinPrice ?? tracks[0]?.downloadMinPrice ?? 0;
  // All download types: floor at €1.00 so the artist always earns after payment fees
  const effectiveMin = Math.max(minPrice, STRIPE_FLOOR_CENTS);
  const currency = item.downloadCurrency || tracks[0]?.downloadCurrency || "eur";

  // ── Format detection ────────────────────────────────────────────────────────
  // Generic file (PDF, ZIP, EXE …) takes priority over audio URLs.
  const genericFormat = item.fileUrl
    ? item.fileLabel || getFormatLabel(item.fileUrl, "FILE")
    : null;

  // Audio-specific formats (shown only when no generic fileUrl)
  const hiResFormat   = !genericFormat && item.hiResUrl  ? getFormatLabel(item.hiResUrl)  : null;
  const primaryFormat = !genericFormat && item.audioUrl  ? getFormatLabel(item.audioUrl)  : null;
  const videoFormat   = !genericFormat && item.videoUrl  ? getFormatLabel(item.videoUrl)  : null;

  // Format picker is only shown for audio when BOTH hi-res and compressed exist
  const hasFormatChoice = !genericFormat && !!(hiResFormat && primaryFormat);

  // Multi-file bundle (marketplace download files)
  const hasMultiFile = !!(item.downloadFiles && item.downloadFiles.length > 0);

  // selectedFormat: 'hires' → serve hiResUrl; 'mp3' → serve audioUrl
  const [selectedFormat, setSelectedFormat] = useState<"hires" | "mp3">(
    hiResFormat ? "hires" : "mp3"
  );

  // Active format label for button / dialog display
  const activeFormatLabel =
    genericFormat ||
    (selectedFormat === "hires"
      ? hiResFormat || primaryFormat || videoFormat || "Audio"
      : primaryFormat || hiResFormat || videoFormat || "Audio");

  // ── Payment state ───────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [userPrice, setUserPrice] = useState<number | "">(effectiveMin);
  const [priceRaw, setPriceRaw] = useState<string>((effectiveMin / 100).toFixed(2));
  const [buyerName, setBuyerName] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showOnBoard, setShowOnBoard] = useState(true);
  const [swishOpen, setSwishOpen] = useState(false);
  const [swishAmountSEK, setSwishAmountSEK] = useState(0);
  const showSwish = isSwedish && currency === "sek";
  const symbol = CURRENCY_SYMBOLS[currency] || "€";

  if (!allowDl) return null;

  // ── Admin / owner shortcut — bypass payment ───────────────────────────────
  // Super admins and the work's owner can download directly without going
  // through the paywall. Uses the /api/download `idToken` query bypass.
  const SUPER_ADMIN_EMAILS = ["breakinbreadswe@gmail.com", "gordoncyrus@gmail.com", "info@luvlab.io"];
  const userEmail = (user?.email || "").toLowerCase().trim();
  const itemOwner = (item as any).owner;
  const isOwner = !!(itemOwner?.userId && user?.uid && itemOwner.userId === user.uid);
  const isSuperAdmin =
    SUPER_ADMIN_EMAILS.includes(userEmail) ||
    (userProfile as any)?.accountType?.toLowerCase() === "superadmin" ||
    ((userProfile as any)?.roles || []).some((r: string) => r?.toLowerCase?.() === "superadmin");
  // POLICY: NO bypass. Every user — admins and owners included — goes through
  // the paywall (Stripe checkout). The admin/owner shortcut below is disabled.
  void isOwner; void isSuperAdmin;
  const canBypass = false;

  const adminDirectDownload = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const fmt = item.fileUrl ? "file" : (item.hiResUrl ? "hires" : "mp3");
      window.location.href = `/api/download?workId=${item.id}&idToken=${encodeURIComponent(token)}&format=${fmt}`;
    } catch (e) {
      console.error("Admin download failed:", e);
    }
  };

  // For admins / the owner, skip the whole paywall and render a direct button.
  // Compact = icon-only, matching the paywall variants (the queue rows looked
  // inconsistent for admins because this branch alone kept its text label).
  if (canBypass) {
    return (
      <Button
        onClick={adminDirectDownload}
        size={compact ? "icon" : "default"}
        className={cn(compact ? "h-8 w-8 rounded-full" : "gap-2", className)}
        title={isOwner ? "Download your track (owner bypass)" : "Download (admin bypass)"}
        aria-label="Download"
      >
        <Download className="h-4 w-4" />
        {!compact && `Download${isSuperAdmin && !isOwner ? " (admin)" : ""}`}
      </Button>
    );
  }

  // Auth gate helper — called before any download action
  const requireAuth = (): boolean => {
    if (user) return true;
    setAuthOpen(true);
    return false;
  };

  const handleOpenPwyw = () => {
    // No auth gate — guests can pay via Stripe checkout as a guest
    if (userProfile && !buyerName) {
      const name = (userProfile as any).nickname || userProfile.displayName || "";
      setBuyerName(name);
    }
    const resetPrice =
      typeof userPrice !== "number" || userPrice < effectiveMin
        ? effectiveMin
        : userPrice;
    setUserPrice(resetPrice);
    setPriceRaw((resetPrice / 100).toFixed(2));
    setIsOpen(true);
  };

  const handlePayCheckout = async () => {
    const priceCents = typeof userPrice === "number" ? userPrice : effectiveMin;
    if (priceCents < effectiveMin) {
      alert(
        `Minimum price is ${symbol}${displayPrice(effectiveMin)} — this ensures the artist earns after payment fees.`
      );
      return;
    }
    try {
      const effectiveName = isAnonymous ? "" : buyerName.trim() || "Someone";
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "download",
          workId: item.id,
          title: item.title,
          artist: item.artist || "",
          album: (item as any).album || "",
          coverArtUrl: item.coverArtUrl,
          minPrice: priceCents,
          currency,
          origin: window.location.origin,
          buyerName: effectiveName,
          isAnonymous,
          downloadFormat: genericFormat ? "file" : selectedFormat, // 'hires' | 'mp3' | 'file'
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || "Failed to create checkout");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Failed to create checkout session");
    }
  };

  const handleFixedCheckout = async () => {
    // No auth gate — guests can pay via Stripe checkout as a guest
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "download",
          workId: item.id,
          title: item.title,
          artist: item.artist || "",
          album: (item as any).album || "",
          coverArtUrl: item.coverArtUrl,
          minPrice,
          currency,
          origin: window.location.origin,
          downloadFormat: selectedFormat,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || "Failed to create checkout");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Failed to create checkout session");
    }
  };

  // ── Sign-in gate dialog ─────────────────────────────────────────────────────
  const authDialog = (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="sm:max-w-[360px]">
        <DialogHeader>
          <DialogTitle>Sign in to download</DialogTitle>
          <DialogDescription>
            Create a free account or sign in to download &ldquo;{item.title}&rdquo;.
            Your downloads are saved to your collection.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-2">
          <Button
            onClick={() => {
              setAuthOpen(false);
              router.push(`/signin?next=${encodeURIComponent(window.location.pathname)}`);
            }}
          >
            Sign in
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setAuthOpen(false);
              router.push(`/onboarding?next=${encodeURIComponent(window.location.pathname)}`);
            }}
          >
            Create free account
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // ── Format picker / file list (shown in dialog) ────────────────────────────
  const formatPicker = hasMultiFile ? (
    // Multi-file bundle: list every file with format badge + size
    <div>
      <p className="text-sm font-medium mb-2">Included files</p>
      <div className="space-y-1.5">
        {item.downloadFiles!.map((dl, i) => {
          const fmt = dl.format || getFormatLabel(dl.url, "FILE");
          const sizeLabel = dl.sizeBytes
            ? dl.sizeBytes >= 1_048_576
              ? `${(dl.sizeBytes / 1_048_576).toFixed(1)} MB`
              : `${(dl.sizeBytes / 1024).toFixed(0)} KB`
            : null;
          return (
            <div
              key={i}
              className="flex items-center gap-2 rounded-md border border-border px-2.5 py-2"
            >
              <span className="rounded bg-muted text-muted-foreground text-[10px] font-bold px-1.5 py-0.5 uppercase shrink-0">
                {fmt}
              </span>
              <span className="text-sm truncate flex-1">{dl.fileName}</span>
              {sizeLabel && (
                <span className="text-[11px] text-muted-foreground shrink-0">{sizeLabel}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  ) : hasFormatChoice ? (
    // Audio format picker — WAV vs MP3
    <div>
      <p className="text-sm font-medium mb-2">Format</p>
      <div className="grid grid-cols-2 gap-2">
        {[
          { key: "hires" as const, label: hiResFormat!, sub: "Lossless · Best quality" },
          { key: "mp3"   as const, label: primaryFormat!, sub: "Compressed · Smaller file" },
        ].map(({ key, label, sub }) => (
          <button
            key={key}
            type="button"
            onClick={() => setSelectedFormat(key)}
            className={cn(
              "rounded-lg border p-2.5 text-left transition-colors",
              selectedFormat === key
                ? "border-primary bg-primary/10"
                : "border-border hover:border-primary/40"
            )}
          >
            <p className="text-sm font-bold">{label}</p>
            <p className="text-[11px] text-muted-foreground">{sub}</p>
          </button>
        ))}
      </div>
    </div>
  ) : (
    // Single-format badge (no choice needed)
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "rounded px-2 py-0.5 text-xs font-semibold uppercase",
          hiResFormat
            ? "bg-primary/15 text-primary"
            : "bg-muted text-muted-foreground"
        )}
      >
        {activeFormatLabel}
      </span>
      {hiResFormat && (
        <span className="text-xs text-muted-foreground">Lossless · Best quality</span>
      )}
    </div>
  );

  // ── NAME YOUR PRICE (covers both "free" and "pwyw" types — all pay minimum) ──
  if (downloadType === "free" || downloadType === "pwyw") {
    return (
      <>
        <Button
          size={compact ? "icon" : "sm"}
          variant="outline"
          className={cn(compact ? "h-8 w-8 rounded-full" : "", className)}
          title={compact ? `Download ${activeFormatLabel}` : undefined}
          onClick={handleOpenPwyw}
        >
          <Download className={cn("h-4 w-4", !compact && "mr-1")} />
          {!compact && (
            <>
              {`Download ${activeFormatLabel} · ${symbol}${displayPrice(effectiveMin)}+`}
            </>
          )}
        </Button>

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-[440px] md:max-w-3xl max-sm:fixed max-sm:inset-x-0 max-sm:top-[50px] max-sm:bottom-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!left-0 max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:overflow-y-auto max-sm:h-auto max-sm:pb-[calc(84px+env(safe-area-inset-bottom))]">
            <DialogHeader>
              <DialogTitle>Download · Pay What You Feel</DialogTitle>
            </DialogHeader>
            {/* Desktop: wide, two columns — what you get on the left, paying on
                the right (Gordon 2026-10-31: "download pop up … wider"). */}
            <div className="space-y-4 md:grid md:grid-cols-2 md:gap-6 md:space-y-0">
              <div className="min-w-0 space-y-4">
              {/* Track info */}
              <div className="flex gap-3">
                {item.coverArtUrl && (
                  <div className="w-16 h-16 flex-shrink-0 rounded overflow-hidden">
                    <Image
                      src={item.coverArtUrl}
                      alt={item.title}
                      width={64}
                      height={64}
                      className="w-full h-full object-cover"
                     
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.title}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    {item.artist || "Unknown Artist"}
                  </p>
                </div>
              </div>

              {/* Format picker or badge */}
              {formatPicker}

              {/* File details — everything the buyer is getting */}
              {(() => {
                const d = (item as any).durationSeconds;
                const durStr = d ? `${Math.floor(d / 60)}:${String(Math.round(d % 60)).padStart(2, "0")}` : null;
                const rows: Array<[string, string]> = [];
                const fmt = (item as any).fileFormat || (downloadType === "free" ? "" : "");
                if (fmt) rows.push(["Format", String(fmt).toUpperCase()]);
                if (durStr) rows.push(["Duration", durStr]);
                const genre = (item as any).genre || ((item as any).genres?.[0]);
                if (genre) rows.push(["Genre", genre]);
                if ((item as any).isrc) rows.push(["ISRC", (item as any).isrc]);
                if ((item as any).copyrightYear) rows.push(["Year", String((item as any).copyrightYear)]);
                if (!rows.length) return null;
                return (
                  <div className="rounded-lg border border-border/60 divide-y divide-border/40 text-xs">
                    {rows.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between px-3 py-1.5">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Artist support framing */}
              <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2.5 space-y-1">
                <p className="text-sm font-medium text-foreground">
                  🌾 Pay according to how the music makes you feel — it goes straight to {item.artist || "the artist"}
                </p>
                <p className="text-xs text-muted-foreground">
                  What you think this music is worth to you, and your hope that this maker keeps
                  making — your appreciation, converted into whatever you can spare. Minimum {symbol}{displayPrice(effectiveMin)}.
                  Pay more to become an Angel who keeps the bakers baking.
                </p>
                <p className="text-xs text-muted-foreground/70">
                  You become the owner of a unique, numbered copy of the recording — your name and
                  edition digits stamped into the download. Your very own collector&apos;s copy. 🎫
                </p>
              </div>

              </div>
              <div className="min-w-0 space-y-4">
              {/* Price input — the big, clear, on-theme "Pay What You Feel"
                  moment. The buyer sets the price HERE (Stripe then just charges
                  it), so this is intentionally prominent. */}
              <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-4">
                <p className="text-center text-sm font-bold uppercase tracking-wider text-primary">
                  🌾 Pay What You Feel
                </p>
                <p className="text-center text-[11px] text-muted-foreground mb-3">
                  minimum {symbol}{displayPrice(effectiveMin)}
                </p>
                {/* Quick-pick amounts */}
                <div className="flex flex-wrap justify-center gap-2 mb-3">
                  {[effectiveMin, Math.max(effectiveMin, 300), Math.max(effectiveMin, 500), Math.max(effectiveMin, 1000),
                    Math.max(effectiveMin, 2000), Math.max(effectiveMin, 5000), Math.max(effectiveMin, 10000), Math.max(effectiveMin, 20000)]
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .map((cents) => (
                      <button
                        key={cents}
                        type="button"
                        onClick={() => { setUserPrice(cents); setPriceRaw((cents / 100).toFixed(2)); }}
                        className={cn(
                          "rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors",
                          userPrice === cents
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        {symbol}{displayPrice(cents)}
                      </button>
                    ))}
                </div>
                <div className="flex items-center justify-center gap-2 w-full">
                  <span className="text-3xl font-bold shrink-0">{symbol}</span>
                  <Input
                    type="number"
                    step="0.01"
                    min={effectiveMin / 100}
                    value={priceRaw}
                    onChange={(e) => setPriceRaw(e.target.value)}
                    onBlur={(e) => {
                      const cents = Math.round((parseFloat(e.target.value) || 0) * 100);
                      const clamped = Math.max(cents, effectiveMin);
                      setUserPrice(clamped);
                      setPriceRaw((clamped / 100).toFixed(2));
                    }}
                    className="h-14 w-full min-w-0 max-w-[16rem] text-center !text-2xl sm:!text-3xl font-bold"
                    placeholder={displayPrice(minPrice)}
                  />
                </div>
              </div>

              {/* Name for the Angels wall (/supporters — fans who give the flour) */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  🌾 Appear on the Angels wall{" "}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <Input
                  value={isAnonymous ? "" : buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder="Your name, nickname or alias"
                  disabled={isAnonymous}
                  className="h-9"
                />
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showOnBoard}
                      onChange={(e) => setShowOnBoard(e.target.checked)}
                      className="accent-primary"
                    />
                    Show on board
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="accent-primary"
                    />
                    Stay anonymous
                  </label>
                </div>
              </div>

              {/* Soft sign-in hint for guests */}
              {!user && (
                <p className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
                  💡 <button
                    type="button"
                    className="underline hover:text-foreground transition-colors"
                    onClick={() => router.push(`/signin?next=${encodeURIComponent(window.location.pathname)}`)}
                  >Sign in</button> to save this download to your collection. Or continue as guest below.
                </p>
              )}

              {/* Pay buttons */}
              <div className="flex gap-2 pt-2">
                {showSwish && (
                  <Button
                    className="flex-1 bg-[#00B67A] hover:bg-[#00a06d] text-white font-semibold"
                    onClick={() => {
                      const priceCents = typeof userPrice === "number" ? userPrice : effectiveMin;
                      if (priceCents < effectiveMin) {
                        alert(
                          `Minimum price is ${symbol}${displayPrice(effectiveMin)} — this ensures the artist earns after payment fees.`
                        );
                        return;
                      }
                      setSwishAmountSEK(priceCents / 100);
                      setIsOpen(false);
                      setSwishOpen(true);
                    }}
                    disabled={typeof userPrice !== "number"}
                  >
                    Pay via Swish
                  </Button>
                )}
                <Button
                  className="flex-1"
                  onClick={handlePayCheckout}
                  disabled={typeof userPrice !== "number"}
                >
                  {showSwish
                    ? "Pay via Card"
                    : `Pay ${symbol}${typeof userPrice === "number" ? displayPrice(userPrice) : "—"} & Download`}
                </Button>
              </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <SwishCheckoutDialog
          open={swishOpen}
          onOpenChange={setSwishOpen}
          amountSEK={swishAmountSEK}
          message={`Download: ${item.title}`}
          itemId={item.id}
          itemType="download"
          itemTitle={item.title}
          buyerId={user?.uid}
          buyerEmail={user?.email ?? undefined}
          onSuccess={(instructionId) => {
            setSwishOpen(false);
            window.location.href = `/download-success?swish_id=${encodeURIComponent(instructionId)}&workId=${item.id}&format=${selectedFormat}`;
          }}
        />
        {authDialog}
      </>
    );
  }

  // ── FIXED PRICE ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className={cn("flex flex-col gap-2", className)}>
        {/* Format picker for fixed-price tracks */}
        {!compact && hasFormatChoice && (
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { key: "hires" as const, label: hiResFormat!, sub: "Lossless" },
              { key: "mp3"   as const, label: primaryFormat!, sub: "Compressed" },
            ].map(({ key, label, sub }) => (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedFormat(key)}
                className={cn(
                  "rounded border px-2.5 py-1.5 text-left text-sm transition-colors",
                  selectedFormat === key
                    ? "border-primary bg-primary/10 font-semibold"
                    : "border-border text-muted-foreground hover:border-primary/40"
                )}
              >
                {label}{" "}
                <span className="text-[11px] font-normal opacity-70">{sub}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          {showSwish && (
            <Button
              size={compact ? "icon" : "sm"}
              className={cn(
                "bg-[#00B67A] hover:bg-[#00a06d] text-white font-semibold",
                compact ? "h-8 w-8 rounded-full" : ""
              )}
              title={compact ? `Buy via Swish · ${symbol}${displayPrice(minPrice)}` : undefined}
              onClick={() => {
                // No auth gate — guests can pay via Swish
                setSwishAmountSEK(minPrice / 100);
                setSwishOpen(true);
              }}
            >
              <Download className="h-4 w-4" />
              {!compact && " Swish"}
            </Button>
          )}
          <Button
            size={compact ? "icon" : "sm"}
            variant="outline"
            className={cn(compact ? "h-8 w-8 rounded-full" : "")}
            title={
              compact
                ? `Buy ${activeFormatLabel} · ${symbol}${displayPrice(minPrice)}`
                : undefined
            }
            onClick={handleFixedCheckout}
          >
            <Download className={cn("h-4 w-4", !compact && "mr-1")} />
            {!compact &&
              (showSwish
                ? "Buy via Card"
                : `Buy ${activeFormatLabel} · ${symbol}${displayPrice(minPrice)}`)}
          </Button>
        </div>
      </div>

      <SwishCheckoutDialog
        open={swishOpen}
        onOpenChange={setSwishOpen}
        amountSEK={swishAmountSEK}
        message={`Download: ${item.title}`}
        itemId={item.id}
        itemType="download"
        itemTitle={item.title}
        buyerId={user?.uid}
        buyerEmail={user?.email ?? undefined}
        onSuccess={(instructionId) => {
          setSwishOpen(false);
          window.location.href = `/api/download?workId=${item.id}&swish_id=${encodeURIComponent(instructionId)}&format=${selectedFormat}`;
        }}
      />
      {authDialog}
    </>
  );
}
